import React, { useEffect, useRef, useState } from "react";
import { PublishingGroupSection } from "./PublishingGroups";
import PartnerHeader from "./PartnerHeader";
import authorShopPic from "../../assets/AuthorShopPic.jpg";
import marketplacePic from "../../assets/MarketpagePic.jpg";
import newsletterComposerPic from "../../assets/NewsletterComposerPic.jpg";

const chapterLabelClass =
  "mb-3 inline-block rounded-full border border-bookish-purple/20 bg-bookish-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-bookish-royal";

const prologuePainPoints = [
  {
    icon: "📉",
    title: "Finding Your Readers",
    description:
      "You've poured yourself into a story. Finding the people who will love it can feel like another book's worth of work.",
  },
  {
    icon: "🔍",
    title: "A Place to Be Discovered",
    description:
      "Your book deserves a place on the shelf where curious readers can explore your genre and get to know you.",
  },
  {
    icon: "📧",
    title: "Keeping in Touch",
    description:
      "You want to share the next chapter with your readers. Making time for a newsletter alongside writing and publishing can be a challenge.",
  },
];

const experiencePreviews = [
  { title: "Author Shop Preview", image: authorShopPic },
  { title: "Marketplace View", image: marketplacePic },
  { title: "Newsletter Editor", image: newsletterComposerPic },
];

const AuthorLandingPage = () => {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const previewOpen = selectedIndex !== null;
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const minSwipeDistance = 50;

  const nextImage = () => {
    setSelectedIndex((prev) => (prev === experiencePreviews.length - 1 ? 0 : prev + 1));
  };

  const prevImage = () => {
    setSelectedIndex((prev) => (prev === 0 ? experiencePreviews.length - 1 : prev - 1));
  };

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (touchStart === null || touchEnd === null) return;
    const distance = touchStart - touchEnd;

    if (distance > minSwipeDistance) nextImage();
    if (distance < -minSwipeDistance) prevImage();
  };

  const onMouseDown = (e) => {
    setDragEnd(null);
    setDragStart(e.clientX);
  };

  const onMouseMove = (e) => {
    if (dragStart === null) return;
    setDragEnd(e.clientX);
  };

  const onMouseUp = () => {
    if (dragStart === null || dragEnd === null) {
      setDragStart(null);
      return;
    }
    const distance = dragStart - dragEnd;

    if (distance > minSwipeDistance) nextImage();
    if (distance < -minSwipeDistance) prevImage();
    setDragStart(null);
    setDragEnd(null);
  };

  useEffect(() => {
    if (!previewOpen) return;
    const trigger = document.activeElement;
    const dialog = dialogRef.current;
    const previousOverflow = document.body.style.overflow;
    dialog.showModal();
    closeRef.current.focus();
    document.body.style.overflow = "hidden";
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      if (trigger?.isConnected) trigger.focus();
    };
  }, [previewOpen]);

  return (
    <main className="public-design bg-bookish-cream text-bookish-royal">
      <PartnerHeader audience="author" />

      <section className="px-6 pb-10 pt-10 text-center md:px-10 md:pt-16">
        <div className="mx-auto max-w-4xl">
          <h1 className="font-serif text-4xl font-extrabold leading-tight text-bookish-purple md:text-6xl">
            You Wrote the Book.
            <br />
            Now Let the <em className="text-bookish-royal">Right Readers</em>
            <br/>
            Find It.
          </h1>
          <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-bookish-royal">
            Give your stories a home among independent voices and curious readers.
            Share your books, welcome readers into your world, and stay connected between chapters.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a
              href="/signup?type=author"
              className="inline-flex items-center justify-center rounded-xl border border-bookish-purple bg-bookish-royal px-8 py-4 text-base font-bold text-bookish-white transition-colors hover:bg-bookish-purpleAccent"
            >
              Start Your Free Trial &rarr;
            </a>
            <a
              href="#how"
              className="inline-flex items-center justify-center rounded-xl border border-bookish-purple/30 bg-bookish-lightCream px-8 py-4 text-base font-semibold text-bookish-purple transition-colors hover:bg-bookish-white"
            >
              See How It Works
            </a>
          </div>
          <p className="mt-4 text-sm text-bookish-royal/80">
              <strong>30 days free</strong>, then $29.95/month or $288/year, billed annually (equivalent to $24/month). Cancel anytime. Founding authors lock in this rate.
          </p>
        </div>
      </section>

      <section className="bg-bookish-lightCream px-6 py-10 md:px-10">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <span className={chapterLabelClass}>Prologue</span>
            <h2 className="font-serif text-3xl text-bookish-purple md:text-5xl">The Hardest Part of Indie Publishing <br />
              Isn't Writing the Book...
            </h2>
            <p className="mx-auto mt-4 max-w-3xl text-bookish-purple">
              It's helping your story find its people. After the writing, editing, and publishing,
              you deserve a welcoming place to begin that next chapter.
            </p>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {prologuePainPoints.map((painPoint) => (
              <article
                key={painPoint.title}
                className="flex min-h-[240px] flex-col items-center rounded-2xl border border-bookish-purple/10 bg-bookish-white p-6 text-bookish-royal"
              >
                <div className="text-3xl" aria-hidden="true">
                  {painPoint.icon}
                </div>
                <h3 className="mt-4 text-center text-3xl font-bold leading-tight text-bookish-purple">
                  {painPoint.title}
                </h3>
                <p className="mt-4 text-left text-base leading-7 text-bookish-royal">
                  {painPoint.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-bookish-white px-6 py-12 md:py-16 md:px-10">
        <div className="mx-auto grid max-w-6xl items-center gap-10 md:grid-cols-[1.2fr_1fr]">
          <div>
            <span className={chapterLabelClass}>Chapter One</span>
            <h2 className="font-serif text-3xl text-bookish-purple md:text-5xl">Build Your Reader Circle</h2>
            <p className="mt-5 text-lg leading-8 text-bookish-royal">
              Give readers a place to return to: your books, your updates, and a little of the person behind the pages.
            </p>
            <p className="mt-3 text-lg leading-8 text-bookish-royal">
              Keep your newsletter and reader connections together, ready for the next story you want to share.
            </p>
            <p className="mt-3 text-lg leading-8 text-bookish-royal">
              Sell direct when you want to, or route readers to external retailers — you control the path.
            </p>
          </div>
          <ul className="rounded-2xl border border-bookish-purple/10 bg-bookish-lightCream p-8 text-bookish-royal">
            <li className="mb-3">✓ Direct reader relationships inside the platform</li>
            <li className="mb-3">✓ Built-in list growth and newsletter touchpoints</li>
            <li>✓ Flexible sales model: direct checkout or retailer links</li>
          </ul>
        </div>
      </section>

      <section className="bg-bookish-lightCream px-6 py-12 md:py-16 md:px-10">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <span className={chapterLabelClass}>Chapter Two</span>
            <h2 className="font-serif text-3xl text-bookish-purple md:text-5xl">See the Author Experience</h2>
            <p className="mx-auto mt-4 max-w-3xl text-bookish-royal">
              Explore key author workflows from storefront setup to newsletter creation.
            </p>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {experiencePreviews.map((preview, index) => (
              <figure
                key={preview.title}
                className="group overflow-hidden rounded-2xl border border-bookish-purple/10 bg-bookish-white"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <button type="button" className="h-full w-full" aria-label={`Open ${preview.title}`} aria-haspopup="dialog" onClick={() => setSelectedIndex(index)}>
                  <img
                    src={preview.image}
                    alt={preview.title}
                    loading="lazy"
                    className="h-full w-full cursor-pointer object-contain p-2 transition-transform duration-300 group-hover:scale-105"
                  />
                  </button>
                </div>
                <figcaption className="px-5 py-4 text-center font-semibold text-bookish-royal">
                  {preview.title}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {selectedIndex !== null && (
        <dialog
          ref={dialogRef}
          aria-labelledby="author-preview-title"
          aria-describedby="author-preview-help"
          className="author-preview-dialog"
          onCancel={event => { event.preventDefault(); setSelectedIndex(null); }}
          onClick={event => { if (event.target === event.currentTarget) setSelectedIndex(null); }}
          onKeyDown={event => {
            if (event.key === "ArrowRight") { event.preventDefault(); nextImage(); }
            if (event.key === "ArrowLeft") { event.preventDefault(); prevImage(); }
          }}
        >
          <div
            className="author-preview-content"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
          >
            <button
              ref={closeRef}
              type="button"
              onClick={() => setSelectedIndex(null)}
              className="author-preview-close"
              aria-label="Close image preview"
            >
              Close ✕
            </button>

            <h2 id="author-preview-title">{experiencePreviews[selectedIndex].title}</h2>

            <img
              src={experiencePreviews[selectedIndex].image}
              alt={experiencePreviews[selectedIndex].title}
              className="author-preview-image"
              draggable={false}
            />

            <div className="author-preview-controls">
            <button type="button" onClick={prevImage} aria-label="Previous image">← Previous</button>
            <span role="status" aria-live="polite">{selectedIndex + 1} / {experiencePreviews.length}</span>
            <button type="button"
              onClick={nextImage}
              aria-label="Next image"
            >
              Next →
            </button>

            </div>
            <p id="author-preview-help">Use the buttons, swipe, or press the arrow keys to explore. Press Escape to close.</p>
          </div>
        </dialog>
      )}

      <section id="features" className="bg-bookish-cream px-6 py-12 md:py-16 md:px-10">
        <div className="mx-auto max-w-6xl">
          <div className="mb-14 text-center">
            <span className={chapterLabelClass}>Chapter Three</span>
            <h2 className="font-serif text-3xl font-bold text-bookish-purple md:text-5xl">
              A Home for Your Books. Room for Your Readers.
            </h2>
            <p className="mx-auto mt-4 max-w-3xl text-lg text-bookish-royal">
              From your first book to your next series, bring your shop, newsletter, and reader connections together.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {[
              {
                title: "Direct-to-Reader Sales (Optional)",
                body: "Sell through your Bookish Circle shop, or link to external retailers when that fits your publishing plans.",
              },
              {
                title: "Author Storefront + Subdomain Shop",
                body: "Launch a branded storefront under your own Bookish Circle subdomain with your books, bio, and links all in one polished destination.",
              },
              {
                title: "Built-In Newsletter System",
                body: "Create and send newsletters from your Author Portal. Share a release, a work in progress, or a note from your writing desk.",
              },
              {
                title: "Genre + Author Follow System",
                body: "Readers can follow you and their favorite genres, then find book updates in their Circle activity.",
              },
            ].map((feature, index) => (
              <div key={feature.title} className="rounded-2xl border border-bookish-purple/10 bg-bookish-white p-8">
                <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-bookish-lightCream font-extrabold text-bookish-purple">
                  {index + 1}
                </div>
                <h3 className="text-xl font-semibold text-bookish-purple">{feature.title}</h3>
                <p className="mt-2 leading-7 text-bookish-royal">{feature.body}</p>
              </div>
            ))}
          </div>
          <aside className="mt-10 rounded-2xl border border-bookish-purple/20 bg-bookish-lightCream p-6 md:p-8" aria-labelledby="author-appreciation-title">
            <h3 id="author-appreciation-title" className="font-serif text-2xl text-bookish-purple">A Little Thank-You for Your Readers</h3>
            <p className="mt-3 leading-7">The optional Reader Appreciation Program lets you offer signed-in Circle Readers $1 off each ebook priced $3 or more and 10% off physical books and merchandise in your Bookish Circle shop. Shipping stays the same.</p>
            <p className="mt-3 leading-7">You choose whether to participate. Eligible discounts apply automatically when enrolled, so you can thank your readers without creating coupon codes.</p>
            <a className="public-text-link" href="/reader-appreciation">Explore Reader Appreciation →</a>
          </aside>
        </div>
      </section>

      <section id="how" className="bg-bookish-royal px-6 py-12 md:py-16 text-bookish-white md:px-10">
        <div className="mx-auto max-w-5xl text-center">
          <span className="mb-3 inline-block rounded-full border border-bookish-white/30 bg-bookish-purpleAccent px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em]">
            Chapter Four
          </span>
          <h2 className="font-serif text-3xl md:text-5xl">Begin Your Next Chapter in Three Steps</h2>
          <div className="mt-12 grid gap-10 md:grid-cols-3">
            <div>
              <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full border border-bookish-white/30 bg-bookish-purpleAccent text-xl font-extrabold">
                1
              </div>
              <h3 className="text-lg font-semibold">Claim Your Circle</h3>
              <p className="mt-2 text-bookish-white/85">
                Create your account, start your membership trial, and follow the setup guide to choose your author name and shop address.
              </p>
            </div>
            <div>
              <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full border border-bookish-white/30 bg-bookish-purpleAccent text-xl font-extrabold">
                2
              </div>
              <h3 className="text-lg font-semibold">Build Your Bookstore</h3>
              <p className="mt-2 text-bookish-white/85">
                Add your covers and descriptions, introduce yourself, and choose retailer links or set up direct checkout.
              </p>
            </div>
            <div>
              <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full border border-bookish-white/30 bg-bookish-purpleAccent text-xl font-extrabold">
                3
              </div>
              <h3 className="text-lg font-semibold">Get Discovered</h3>
              <p className="mt-2 text-bookish-white/85">
                Share your shop, invite readers to follow you, and keep them close with book updates and newsletters.
              </p>
            </div>
          </div>
        </div>
      </section>

      <PublishingGroupSection />

      <section id="claim" className="bg-bookish-white px-6 py-12 md:py-16 md:px-10">
        <div className="mx-auto max-w-3xl text-center">
          <span className={chapterLabelClass}>Chapter Five</span>
          <h2 className="font-serif text-3xl text-bookish-purple md:text-5xl">Simple Pricing. Serious Value.</h2>
          <p className="mt-3 text-lg text-bookish-royal">
            A place for your books and reader connections, with a founding membership to help you begin.
          </p>
          <div className="mt-9 rounded-2xl border border-bookish-purple/10 bg-bookish-lightCream p-10">
            <div className="mt-2 text-xl text-bookish-royal">
              <span className="mr-2 rounded-full bg-bookish-purple px-3 py-1 text-xs font-bold uppercase tracking-wide text-bookish-white">
                Founding Author
              </span>
              <span>$29.95/month or $288/year</span>
            </div>
            <p className="mt-3">Annual membership is billed yearly, equivalent to $24/month.</p>
            <div className="mt-3 font-serif text-5xl font-extrabold text-bookish-purple">30 Days Free</div>
            <p className="mt-2 text-sm text-bookish-royal/80">
            Billing starts after trial.
            </p>
            <ul className="mt-6 space-y-3 text-left text-bookish-royal">
              <li className="border-b border-bookish-purple/10 pb-3">✓ Branded author storefront and subdomain shop</li>
              <li className="border-b border-bookish-purple/10 pb-3">✓ Direct-to-reader sales (optional) + retailer links</li>
              <li className="border-b border-bookish-purple/10 pb-3">✓ Built-in newsletter system</li>
              <li className="border-b border-bookish-purple/10 pb-3">✓ Genre and author follow notifications</li>
              <li className="border-b border-bookish-purple/10 pb-3">✓ Optional Reader Appreciation participation</li>
              <li>✓ Priority support for founding members</li>
            </ul>
            <a
              href="/signup?type=author&tier=founding&billing=monthly"
              className="mt-8 inline-flex items-center justify-center rounded-xl border border-bookish-purple bg-bookish-royal px-8 py-4 font-bold text-bookish-white transition-colors hover:bg-bookish-purpleAccent"
            >
              Start Your Free Trial &rarr;
            </a>
            <p className="mt-4 text-sm text-bookish-royal/80">
              Start with 30 days free. Earn a completion reward while places remain to extend your trial. Founding authors lock in this rate. Billing requires your consent and a saved card.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-bookish-lightCream px-6 py-12 md:py-16 md:px-10">
        <div className="mx-auto max-w-4xl">
          <span className={chapterLabelClass}>Chapter Six</span>
          <h2 className="text-center font-serif text-3xl text-bookish-purple md:text-5xl">Questions Authors Ask Before Joining</h2>

          {[
            {
              q: "I already sell on Amazon. Why do I need the Bookish Circle?",
              a: "Keep the retailers that work for you. Your Circle shop gives readers another way to discover your books, follow your updates, and join your newsletter. You can link to Amazon from your storefront.",
            },
            {
              q: "How is this different from a Goodreads page or a personal website?",
              a: "Your author shop is part of a shared bookish community. Readers can browse the marketplace, follow authors and genres, and find updates in their Circle activity — with a path back to your books.",
            },
            {
              q: "I only have one or two books. Is it worth it?",
              a: "One book is enough to begin your corner of the Circle. Introduce yourself, share that story, and give interested readers a way to stay in touch as you write the next one.",
            },
            {
              q: "What if I already have a newsletter?",
              a: "Keep what works for you. Readers who find you here can join your Circle newsletter, giving you another place to share book news and notes from your writing life.",
            },
          ].map((item) => (
            <div key={item.q} className="border-b border-bookish-purple/15 py-6 first:mt-6 first:border-t">
              <h3 className="text-lg font-bold text-bookish-purple">{item.q}</h3>
              <p className="mt-2 text-bookish-royal">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-bookish-royal px-6 py-12 md:py-16 text-center text-bookish-white md:px-10">
        <div className="mx-auto max-w-3xl">
          <span className="mb-3 inline-block rounded-full border border-bookish-white/30 bg-bookish-purpleAccent px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em]">
            Final Chapter
          </span>
          <h2 className="font-serif text-4xl leading-tight md:text-5xl">
            Your Readers Are Out There.
            <br />
            Give Them a Place in Your Story.
          </h2>
          <p className="mt-5 text-bookish-white/85">
            Bring your books, your imagination, and the stories still waiting to be written. Make yourself at home in the Circle.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a
              href="/signup?type=author"
              className="inline-flex items-center justify-center rounded-xl border border-bookish-white bg-bookish-white px-8 py-4 font-bold text-bookish-purple transition-colors hover:bg-bookish-lightCream"
            >
              Start Your Free Trial &rarr;
            </a>
            <a
              href="/market"
              className="inline-flex items-center justify-center rounded-xl border border-bookish-white/60 bg-bookish-purple px-8 py-4 font-semibold text-bookish-white transition-colors hover:bg-bookish-purpleAccent"
            >
              Explore Bookish Circle Market
            </a>
          </div>
        </div>
      </section>

    </main>
  );
};

export default AuthorLandingPage;
